import { toast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useTranslationUpdateCountryMutation } from "@/v2-backoffice-client/translation/countries/translation.updateCountry.mutation"
import {
  COUNTRY_PUBLISH_NOTE,
  COUNTRY_PUBLISH_TOAST,
} from "@/v2-backoffice-client/translation/countries/translation.countries.constants"
import {
  countryNameGap,
  countryReadiness,
  type TranslationCountryRow,
} from "@/v2-backoffice-client/translation/countries/translation.countries.utils"

export type TranslationCountriesSupportToggleProps = {
  row: TranslationCountryRow
}

/**
 * The market switch. Supporting a country is what puts it in the console's
 * project picker, in a user's selectable context, and in the country
 * availability list of every feature, basemap and nav layer.
 *
 * Two things the confirm copy has to carry, because neither is visible from
 * the table: the edit is authored, not live (it ships with the next
 * publish), and a country can be supported while still missing its geometry
 * or some of its names.
 */
function TranslationCountriesSupportToggle(
  props: TranslationCountriesSupportToggleProps,
) {
  const { row } = props
  const [updateCountry, updateCountryState] =
    useTranslationUpdateCountryMutation()

  const enabling = !row.isSupported
  const readiness = countryReadiness(row)
  const nameGap = countryNameGap(row)

  async function onConfirm() {
    try {
      await updateCountry({
        variables: { input: { Code: row.code, IsSupported: enabling } },
      })
      toast({
        title: `${row.name} is now ${enabling ? "supported" : "unsupported"} in the catalog.`,
        description: COUNTRY_PUBLISH_TOAST,
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: `Failed to ${enabling ? "support" : "unsupport"} ${row.name}`,
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const publishNote = COUNTRY_PUBLISH_NOTE

  // The name gap is mentioned but never blocks: it is the normal state of
  // every country in the catalog, the live markets included.
  const nameNote = nameGap
    ? ` It is ${nameGap}; the console falls back to English for the rest.`
    : ""

  const confirmContent = enabling
    ? readiness.isReady
      ? `Support ${row.name}? It becomes selectable for projects and user contexts, and joins every feature's country availability list.${nameNote} ${publishNote}`
      : `${row.name} has no map geometry, so the map has no extent to open it at. Supporting it anyway is allowed, but that gap has no fallback.${nameNote} ${publishNote}`
    : `Stop supporting ${row.name}? It leaves the pickers and availability lists. Projects and users already pointing at it are NOT migrated. ${publishNote}`

  return (
    <ConfirmButton
      size="sm"
      variant={enabling ? "primary" : "outline"}
      disabled={updateCountryState.loading}
      isLoading={updateCountryState.loading}
      content={
        enabling
          ? "Open the market (location.Countries.IsSupported)."
          : "Close the market; the country stays in the catalog as reference data."
      }
      confirmContent={confirmContent}
      confirmVariant={
        enabling && !readiness.isReady ? "destructive" : undefined
      }
      onConfirm={onConfirm}
      data-feature-code="UI_BackofficeTranslation_Countries_ToggleSupport"
    >
      {enabling ? "Support" : "Unsupport"}
    </ConfirmButton>
  )
}

export default TranslationCountriesSupportToggle
